import React, { useMemo } from 'react';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import strings from '../Globalization/index';
import connectionService from '../services/Connection';
import consts from '../consts';
import util from '../util/index';
import { useBreadcrumbs } from '../contexts/Breadcrumbs';
import Accordion from '../components/common/Accordion';
import { useUniqueEffect, useSetState } from '../hooks';
import ChangePasswordForm from './forms/ChangePasswordForm';

const { regex } = consts;

const UserInfo = () => {
	const [, setBreadcrumbsState] = useBreadcrumbs();
	const auth = useSelector((state) => state.auth);
	const userCanChangePassword = useMemo(() => {
		const { tags } = auth;
		return tags?.RoleName.toLowerCase() !== 'superadmin';
	}, [auth]);
	const [state, setState] = useSetState({
		loading: false,
	});

	const { tags: userData = {} } = auth;

	const onChangePasswordSubmit = async (values, { resetForm }) => {
		const formData = new FormData();
		formData.append('action', 'changepassword');
		formData.append('OldPassword', values.OldPassword);
		formData.append('NewPassword', values.NewPassword);
		formData.append('ConfirmPassword', values.ConfirmPassword);

		fetch(connectionService.Login, { body: formData, method: 'POST' })
			.then((res) => res.json())
			.then((json) => {
				setState({ loading: false });
				if (!json?.success) {
					Swal.fire({ titleText: strings.Error, text: strings[json.info], icon: 'error', confirmButtonText: strings.Ok, confirmButtonColor: '#3085d6' });
					return json;
				}
					if (json.info === consts.ResponseText.PasswordChanged) {
						Swal.fire({ titleText: strings.Info, text: strings[json.info], icon: 'success', confirmButtonText: strings.Ok, });
						resetForm();
					} else {
						Swal.fire({ titleText: strings.Error, text: strings[json.info], icon: 'error', confirmButtonText: strings.Ok, confirmButtonColor: '#3085d6' });
				}
				return json;
			})
			.catch((err) => {
				Swal.fire({
					titleText: strings.Error,
					text: strings.ErrorUpdate || 'Some error occured during update',
					icon: 'error',
					confirmButtonText: strings.Ok,
					confirmButtonColor: '#3085d6',
					allowOutsideClick: false
				});
			})
			.finally(() => {
				setState({ loading: false });
			});
	};
	useUniqueEffect(() => {
		setBreadcrumbsState({
			breadcrumbs: [{ text: 'UserInfo' }],
			subbreadcrumbs: [{ text: 'User' }, { text: userData.Username || '' }],
			hideHelp: true,
			docTitle: strings.UserInfo
		});
	}, [userData.Username]);

		const dateTimeFormat = util.getDateTimeFormat(),
		lastLogin = userData.LastLoginOn ? dayjs(userData.LastLoginOn.replace(regex.DateExtract, "")).format(dateTimeFormat.date) : '',
		roleName = auth?.tags?.RoleName.toLowerCase() === consts.RoleName.SuperAdmin.toLowerCase() ? strings.SterisAdmin : auth?.tags?.RoleName;

	return (
		<Card>
			<CardContent>
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: '0.4fr 0.03fr 3fr',
						gridTemplateRows: '1fr 1fr 1fr 1fr',
						gap: (theme) => theme.spacing(1, 0.5),
						gridTemplateAreas: '". . ."\n    ". . ."\n    ". . ."\n    ". . ."',
						marginBottom: theme => theme.spacing(2),
						padding: theme => theme.spacing()
					}}
				>
					<Typography variant="subtitle2">{strings.Username}</Typography>
					<Typography>:</Typography>
					<Typography variant="body2">{userData.Username}</Typography>

					<Typography variant="subtitle2">{strings.PrimaryEmail}</Typography>
					<Typography>:</Typography>
					<Typography variant="body2">{userData?.PrimaryEmail}</Typography>

					<Typography variant="subtitle2">{strings.Role}</Typography>
					<Typography>:</Typography>
					<Typography variant="body2">{roleName}</Typography>

					<Typography variant="subtitle2">{strings.LastLoginOn}</Typography>
					<Typography>:</Typography>
					<Typography variant="body2">{lastLogin}</Typography>
				</Box>
				{userCanChangePassword && (<Accordion title={strings.ChangePassword || 'Change Password'}>
					<ChangePasswordForm
						activeRecord={userData}
						saveBtnText="Ok"
						hideCancel
						onSubmit={onChangePasswordSubmit}
						containerProps={{ elevation: 0 }}
					/>
				</Accordion>)}
			</CardContent>
		</Card>
	);
};

export default UserInfo;
